import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, Film, Tv, Star, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { searchMedia, getImageUrl } from '@/lib/api';

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const SearchResult = ({ item, index }) => {
  const title = item.title || item.name;
  const poster = getImageUrl(item.poster_path, 'w185');
  const isMovie = item.media_type === 'movie';
  const isTv = item.media_type === 'tv';
  const isPerson = item.media_type === 'person';
  
  if (isPerson) return null;

  const year = isMovie 
    ? item.release_date?.slice(0, 4) 
    : item.first_air_date?.slice(0, 4);
  
  return (
    <Link 
      to={`/${isMovie ? 'movie' : 'tv'}/${item.id}`}
      data-testid={`search-result-${item.id}`}
      className="flex gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-indigo-500/30 transition-all duration-200 animate-fade-in"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* Poster */}
      <div className="w-16 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-800">
        {poster ? (
          <img src={poster} alt={title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {isMovie ? <Film className="w-6 h-6 text-gray-600" /> : <Tv className="w-6 h-6 text-gray-600" />}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-white line-clamp-1">{title}</h3>
          <span className={`text-xs px-2 py-1 rounded-full ${isMovie ? 'bg-indigo-500/20 text-indigo-300' : 'bg-purple-500/20 text-purple-300'}`}>
            {isMovie ? 'Film' : 'Serie'}
          </span>
        </div>
        
        <div className="flex items-center gap-3 mt-1 text-sm text-gray-400">
          {year && <span>{year}</span>}
          {item.vote_average > 0 && (
            <span className="flex items-center gap-1">
              <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
              {item.vote_average.toFixed(1)}
            </span>
          )}
        </div>
        
        {item.overview && (
          <p className="text-sm text-gray-500 line-clamp-2 mt-2">{item.overview}</p>
        )}
      </div>
    </Link>
  );
};

export const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [mediaType, setMediaType] = useState(searchParams.get('type') || 'multi');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const performSearch = useCallback(async (searchQuery, type) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }

    setLoading(true);
    setSearched(true);
    
    try {
      const data = await searchMedia(searchQuery, type);
      setResults(data.results?.filter(item => item.media_type !== 'person') || []);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((q, t) => performSearch(q, t), 300),
    [performSearch]
  );

  useEffect(() => {
    if (query) {
      debouncedSearch(query, mediaType);
      setSearchParams({ q: query, type: mediaType });
    }
  }, [query, mediaType, debouncedSearch, setSearchParams]);

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setSearched(false);
    setSearchParams({});
  };

  return (
    <div data-testid="search-page" className="min-h-screen p-4 md:p-8 lg:p-12">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <h1 className="text-3xl md:text-4xl font-display font-bold mb-6">Zoeken</h1>
        
        {/* Search Input */}
        <div className="relative">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input 
            data-testid="search-input"
            type="text"
            placeholder="Zoek films en series..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-12 pr-12 py-6 text-lg bg-white/5 border-white/10 rounded-xl focus:border-indigo-500 focus:ring-indigo-500"
          />
          {query && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          )}
        </div>

        {/* Type Filter */}
        <div className="mt-4">
          <Tabs value={mediaType} onValueChange={setMediaType}>
            <TabsList className="bg-white/5 border border-white/10">
              <TabsTrigger 
                value="multi" 
                data-testid="filter-all"
                className="data-[state=active]:bg-indigo-600"
              >
                Alles
              </TabsTrigger>
              <TabsTrigger 
                value="movie" 
                data-testid="filter-movies"
                className="data-[state=active]:bg-indigo-600"
              >
                Films
              </TabsTrigger>
              <TabsTrigger 
                value="tv" 
                data-testid="filter-series"
                className="data-[state=active]:bg-indigo-600"
              >
                Series
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-4xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : results.length > 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-400 mb-4">{results.length} resultaten gevonden</p>
            {results.map((item, index) => (
              <SearchResult key={`${item.media_type}-${item.id}`} item={item} index={index} />
            ))}
          </div>
        ) : searched ? (
          <div className="text-center py-12">
            <p className="text-gray-400">Geen resultaten gevonden voor "{query}"</p>
          </div>
        ) : (
          <div className="text-center py-12">
            <SearchIcon className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-400">Begin met typen om te zoeken</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
